import { getDatabase, ref, push, child, update } from "firebase/database";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import axios from "axios";
import JsZip from "jszip";
import { db, storage } from "../firebase";

const CM_TO_IN = 0.39370079; // 1cm = 0.39370 inches
const IN_TO_CM = 2.54;
const beforeDecimal = (n) => {
  return Math.floor(n);
};

const afterDecimal = (n) => {
  const decimal = n.toString().split(".")[1];
  return parseInt(decimal, 10);
};

const nearest16thFraction = (n) => {
  /*   return Math.round(16 * (n * 0.00001)); */
  let denominator = 16;
  let numerator = Math.round(16 * (n * 0.00001));
  while (numerator && numerator % 2 === 0) {
    numerator /= 2;
    denominator /= 2;
  }
  return { numerator, denominator };
};

const decimalInchesToNearest16thFraction = (inches) => {
  return {
    integer: beforeDecimal(inches),
    fraction: nearest16thFraction(afterDecimal(inches)),
  };
};

const cmToIn = (centimeters) => {
  const inches = centimeters * CM_TO_IN;
  return inches.toFixed(5);
};

const inchFractionsToCmDecimal = ({ integer, fraction }) => {
  const afterDecimal = fraction.numerator / fraction.denominator || 0;
  const inchesDecimal = integer + afterDecimal;
  const centimeters = inchesDecimal * IN_TO_CM;
  return centimeters.toFixed(2);
};

const convertFractionStringToObject = (input) => {
  if (input === "0") return { numerator: 0, denominator: 16 };
  else {
    let parts = input.split("/");
    let numerator = parseInt(parts[0]);
    let denominator = parseInt(parts[1]);
    return { numerator, denominator };
  }
};

const notify = (message) => {
  let el = document.getElementById("notification-box");
  let wrapper = el.firstChild;
  wrapper.setAttribute("data-before", message);
  el.style.opacity = 1;
  setTimeout(() => {
    el.style.opacity = 0;
  }, 3000);
};

const autosaveInsert = () => {
  const insertJson = window?.saveInsert?.(true);
  sessionStorage.setItem("autosaved_insert", insertJson);
  notify("Drawer insert auto saved.");
};

const stripMaterialTitle = (title) => {
  return title.split("(")[1].split(",")[0];
};

const zipAndStoreFilesToFirebase = async (items) => {
  try {
    const zip = new JsZip();

    // Compress the insert JSON strings
    const streamPromises = items
      .filter((item) => item.svgs)
      .map((item) => {
        const stream = new Blob(
          [Buffer.from(item.svgs.insert, "base64").toString()],
          {
            type: "application/json",
          }
        ).stream();
        const compressedReadableStream = stream.pipeThrough(
          new CompressionStream("gzip")
        );
        return new Response(compressedReadableStream);
      });

    const streams = await Promise.all(streamPromises);

    const blobPromises = streams.map((stream) => stream.blob());

    const blobs = await Promise.all(blobPromises);

    const arrayBufferPromises = blobs.map((blob) => blob.arrayBuffer());

    const arrayBuffers = await Promise.all(arrayBufferPromises);

    items
      .filter((item) => item.svgs)
      .forEach((item, i) => {
        const folder = zip.folder(
          i + 1 + "-" + item.title.split(" ").join("-").toLowerCase()
        );
        folder.file(
          `${item.title} Image.jpg`,
          Buffer.from(item.svgs.pic, "base64").toString().split("base64,")[1],
          { base64: true }
        );
        folder.file(
          `${item.title} Insert.json.gz`,
          new Uint8Array(arrayBuffers[i]),
          { base64: true }
        );
        folder.file(
          `${item.title} Dividers.svg`,
          Buffer.from(item.svgs.dividers, "base64").toString()
        );
        if (item.bottomRequired) {
          folder.file(
            `${item.title} Base.svg`,
            Buffer.from(item.svgs.base, "base64").toString()
          );
        }
      });

    const content = await zip.generateAsync({
      type: "base64",
      compression: "DEFLATE",
      compressionOptions: {
        level: 9,
      },
    });

    const newShareKey = push(child(ref(db), "files")).key;

    const updates = {};
    updates["/files/" + newShareKey] = content;
    update(ref(db), updates);
    return newShareKey;
  } catch (error) {
    console.trace(error.message);
  }
};

const getCartTotalAmount = (cart) => {
  let total = 0;
  for (let i = 0; i < cart.length; i++) {
    total += cart[i].quantity;
  }
  return total;
};

const returnShopifyProductPrice = (baseProduct) => {
  return parseInt(baseProduct.node.priceRange.minVariantPrice.amount);
};

const saveCart = (cart) => {
  if (!("indexedDB" in window)) {
    // Can't use IndexedDB
    console.log("This browser doesn't support IndexedDB");
    return;
  }

  let db = null;

  const openRequest = indexedDB.open("cart_db", 1);

  openRequest.onupgradeneeded = function (e) {
    db = e.target.result;

    db.createObjectStore("cart_os", { keyPath: "id" });
  };

  openRequest.onerror = function () {
    console.error("failed to open cart db");
  };

  openRequest.onsuccess = function (e) {
    db = e.target.result;
    const transaction = db.transaction("cart_os", "readwrite");

    const objectStore = transaction.objectStore("cart_os");
    objectStore.put({ id: 1, cart });
    db.close();
  };
};

const readCart = () => {
  return new Promise((resolve) => {
    try {
      let db = null;
      const openRequest = indexedDB.open("cart_db", 1);

      openRequest.onupgradeneeded = function (e) {
        db = e.target.result;

        db.createObjectStore("cart_os", { keyPath: "id" });
      };

      openRequest.onsuccess = function (e) {
        db = e.target.result;
        const transaction = db.transaction("cart_os", "readonly");

        const cart = transaction.objectStore("cart_os").get(1);
        cart.onsuccess = function () {
          db.close();
          resolve(cart.result || { cart: [] });
        };
      };
    } catch (error) {
      resolve({ cart: [] });
    }
  });
};

const saveMaterials = (materials) => {
  if (!("indexedDB" in window)) {
    // Can't use IndexedDB
    console.log("This browser doesn't support IndexedDB");
    return;
  }

  let db = null;

  const openRequest = indexedDB.open("materials_db", 1);

  openRequest.onupgradeneeded = function (e) {
    db = e.target.result;

    db.createObjectStore("cart_os", { keyPath: "id" });
  };

  openRequest.onerror = function () {
    console.error("failed to open materials db");
  };

  openRequest.onsuccess = function (e) {
    db = e.target.result;
    const transaction = db.transaction("materials_os", "readwrite");

    const objectStore = transaction.objectStore("materials_os");
    objectStore.put({ id: 1, materials });
    db.close();
  };
};

const readMaterials = () => {
  return new Promise((resolve) => {
    try {
      let db = null;
      const openRequest = indexedDB.open("materials_db", 1);

      openRequest.onupgradeneeded = function (e) {
        db = e.target.result;

        db.createObjectStore("materials_os", { keyPath: "id" });
      };

      openRequest.onsuccess = function (e) {
        db = e.target.result;
        const transaction = db.transaction("materials_os", "readonly");

        const materials = transaction.objectStore("materials_os").get(1);
        materials.onsuccess = function () {
          db.close();
          resolve(materials.result || { materials: [] });
        };
      };
    } catch (error) {
      resolve({ materials: [] });
    }
  });
};
const trimProductTitle = (title) => {
  if (title.split("(").length > 1) return title.split("(")[1].split(")")[0];
  else return title;
};

const fetchTemplateFromFirebase = async (storageLocation) => {
  try {
    // Create a reference to the file in Firebase Storage
    const templateRef = storageRef(storage, storageLocation);

    // Get the download URL
    const url = await getDownloadURL(templateRef);

    // Fetch the JSON data
    const response = await fetch(url);
    const templateData = await response.json();

    return templateData;
  } catch (error) {
    console.error("Error loading template:", error.message);
    notify("Error loading template. Please try again.", "error");
    return { error: true }
  }
};

export {
  decimalInchesToNearest16thFraction,
  cmToIn,
  inchFractionsToCmDecimal,
  convertFractionStringToObject,
  autosaveInsert,
  notify,
  stripMaterialTitle,
  zipAndStoreFilesToFirebase,
  getCartTotalAmount,
  returnShopifyProductPrice,
  saveCart,
  readCart,
  saveMaterials,
  readMaterials,
  trimProductTitle,
  fetchTemplateFromFirebase
};
